/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

import Feathers from "@feathersjs/feathers";

import { CustomError, Password } from "@Internal/Models";
import { Autobind } from "@Internal/Utils";

import { ProfileService, ProfileFields, TotpChallenge, Session } from "App/Contracts";
import { User } from "App/Models";

import { ProcessException } from "./Base";

/* Authenticated self-service backed by the "/Internal/Account/*" endpoints. The Feathers client
   attaches the session token globally, so these resolve to the signed-in user on the backend. */
export default class Profile implements ProfileService {
    private profileService: Feathers.Service<any>;
    private passwordService: Feathers.Service<any>;
    private totpService: Feathers.Service<any>;
    private totpBeginService: Feathers.Service<any>;
    private totpEnableService: Feathers.Service<any>;
    private totpDisableService: Feathers.Service<any>;
    private totpRecoveryService: Feathers.Service<any>;
    private sessionsService: Feathers.Service<any>;
    private sessionRevokeService: Feathers.Service<any>;

    constructor(
        profileService: Feathers.Service<any>,
        passwordService: Feathers.Service<any>,
        totpService: Feathers.Service<any>,
        totpBeginService: Feathers.Service<any>,
        totpEnableService: Feathers.Service<any>,
        totpDisableService: Feathers.Service<any>,
        totpRecoveryService: Feathers.Service<any>,
        sessionsService: Feathers.Service<any>,
        sessionRevokeService: Feathers.Service<any>
    ) {
        this.profileService = profileService;
        this.passwordService = passwordService;
        this.totpService = totpService;
        this.totpBeginService = totpBeginService;
        this.totpEnableService = totpEnableService;
        this.totpDisableService = totpDisableService;
        this.totpRecoveryService = totpRecoveryService;
        this.sessionsService = sessionsService;
        this.sessionRevokeService = sessionRevokeService;
    }

    @Autobind
    async Get(): Promise<[ User | undefined, CustomError | undefined ]> {
        try {
            const results = await this.profileService.find();

            if( results !== undefined && results.Error !== undefined )
                return [ undefined, new CustomError( results.Error.Id, results.Error.Message ) ];

            const user = new User( results.Id, results.Email );
            user.FirstName = results.FirstName;
            user.LastName = results.LastName;
            user.Phone = results.Phone;

            return [ user, undefined ];

        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }

    @Autobind
    async Update( fields: ProfileFields ): Promise<CustomError | undefined> {
        try {
            /* The backend resolves the user from the session and ignores this id. */
            const results = await this.profileService.update( 0, {
                FirstName: fields.FirstName,
                LastName: fields.LastName,
                Phone: fields.Phone
            });

            if( results !== undefined && results.Error !== undefined )
                return new CustomError( results.Error.Id, results.Error.Message );

        } catch( error ) {
            return ProcessException( error );
        }

        return undefined;
    }

    @Autobind
    async ChangePassword(
        current: Password,
        next: Password
    ): Promise<CustomError | undefined> {
        try {
            const results = await this.passwordService.create({
                CurrentPassword: current.Value,
                NewPassword: next.Value
            });

            if( results !== undefined && results.Error !== undefined )
                return new CustomError( results.Error.Id, results.Error.Message );

        } catch( error ) {
            return ProcessException( error );
        }

        return undefined;
    }

    @Autobind
    async TotpStatus(): Promise<[ boolean, CustomError | undefined ]> {
        try {
            const results = await this.totpService.find();

            if( results !== undefined && results.Error !== undefined )
                return [ false, new CustomError( results.Error.Id, results.Error.Message ) ];

            return [ results.Enabled === true, undefined ];

        } catch( error ) {
            return [ false, await ProcessException( error ) ];
        }
    }

    @Autobind
    async TotpBegin(): Promise<[ TotpChallenge | undefined, CustomError | undefined ]> {
        try {
            const results = await this.totpBeginService.create({});

            if( results !== undefined && results.Error !== undefined )
                return [ undefined, new CustomError( results.Error.Id, results.Error.Message ) ];

            return [
                {
                    ManualKey: results.ManualKey,
                    KeyUri: results.KeyUri,
                    Account: results.Account,
                    Issuer: results.Issuer
                },
                undefined
            ];

        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }

    @Autobind
    async TotpEnable( code: string ): Promise<[ string[] | undefined, CustomError | undefined ]> {
        return this.recoveryCall(
            (): Promise<any> => this.totpEnableService.create( { Code: code } )
        );
    }

    @Autobind
    async TotpDisable(): Promise<CustomError | undefined> {
        try {
            const results = await this.totpDisableService.create({});

            if( results !== undefined && results.Error !== undefined )
                return new CustomError( results.Error.Id, results.Error.Message );

        } catch( error ) {
            return ProcessException( error );
        }

        return undefined;
    }

    @Autobind
    async TotpRecovery(): Promise<[ string[] | undefined, CustomError | undefined ]> {
        return this.recoveryCall( (): Promise<any> => this.totpRecoveryService.create({}) );
    }

    @Autobind
    async Sessions(): Promise<[ Session[], CustomError | undefined ]> {
        try {
            const results = await this.sessionsService.find();

            if( results !== undefined && results.Error !== undefined )
                return [ [], new CustomError( results.Error.Id, results.Error.Message ) ];

            const list: Session[] = Array.isArray( results ) ? results : [];

            return [ list, undefined ];

        } catch( error ) {
            return [ [], await ProcessException( error ) ];
        }
    }

    @Autobind
    async RevokeSession( id: number ): Promise<CustomError | undefined> {
        try {
            const results = await this.sessionRevokeService.create( { Id: id } );

            if( results !== undefined && results.Error !== undefined )
                return new CustomError( results.Error.Id, results.Error.Message );

        } catch( error ) {
            return ProcessException( error );
        }

        return undefined;
    }

    /* Enable and Regenerate both return the recovery codes as a comma-joined string (the formatter
       mangles arrays of scalars), so split it back the same way. */
    private async recoveryCall(
        call: () => Promise<any>
    ): Promise<[ string[] | undefined, CustomError | undefined ]> {
        try {
            const results = await call();

            if( results !== undefined && results.Error !== undefined )
                return [ undefined, new CustomError( results.Error.Id, results.Error.Message ) ];

            const raw = results.RecoveryCodes;
            const codes: string[] = typeof raw === "string" && raw !== ""
                ? raw.split( "," )
                : ( Array.isArray( raw ) ? raw : [] );

            return [ codes, undefined ];

        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }
}
